import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { flushSync } from 'react-dom';
import useDialogA11y from '../../hooks/useDialogA11y';
import useScrolledAway from '../../hooks/useScrolledAway';
import { useLocation, useNavigate, useNavigationType } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { getMe, onboardCustomer, sendOtp, verifyOtp } from '../../api/storeApi';
import { useAuth } from '../../Context/AuthContext';
import { toImageUrl } from '../../Store/Actions/getSalon';
import { haptic } from '../../utils/haptics';
import { journeyPageOf } from '../../utils/shopPageOf';
import { rememberOrigin, takeOrigin, zoomBackTo, zoomInto } from '../../utils/zoom';
import { normalizePhone } from './agentPhone';
import { decideAgentProposal, mintAgentVisitorKey, sendAgentMessage, shrinkImage, synthesizeAgentReply, transcribeAgentVoice } from './agentApi';
import { RecordingStrip, VoiceButton, VoiceModeView, canRecordClips, isTouchDevice, languageOf, useBodyScrollLock, useHoldToRecord, useVoiceConversation } from '../../voice';
import { addToStorefrontCart } from './agentCart';
import { contactOrderChannels } from '../../utils/contactOrder';
import { trackStoreEvent } from '../../api/storeApi';
import { agentPageContext, groupMessages, loginStep, readVisitorKey, replyCharacter, saveVisitorKey } from './agentChatCore';
import { agentStarters } from './agentStarters';
import FocusBar from './FocusBar';
import LunaCharacter from './LunaCharacter';
import ProductStrip from './ProductStrip';
import ReplyText from './ReplyText';
import ReplyFeedback from './ReplyFeedback';
import SelectionBar from './SelectionBar';
import { AppointmentCards, OrderCards } from './BookingCards';
import { addToCalendar } from '../../utils/calendarFile';
import ServiceStrip from './ServiceStrip';
import StayStrip from './StayStrip';
import { StylonCharacter } from './StylonCharacter';
import ThinkingLine from './ThinkingLine';
import styles from './agentChat.module.css';

/**
 * THE BUSINESS PAGE CHAT — the customer agent (docs/superpowers/specs/2026-10-21-customer-agent-design.md).
 *
 * A short bold answer with More; choices as pills; products as a strip of the shop's own cards; the place with Waze
 * and Maps; links as buttons (the page, a checkout); a sentence to approve with Confirm / Change; a picture to search
 * by ("something like this"); and the login step inside the conversation when the agent needs the customer — phone,
 * code, and a name for a brand-new customer.
 *
 * THE FEEL (DESIGN_SYSTEM.md §6, 10-22). The panel grows out of the launcher and shrinks back into it (a short
 * `closing` phase before it leaves); new messages fade in; every tappable thing presses; a haptic confirms what you
 * did (utils/haptics: light on open, medium on send, selection on a choice, success when a confirmed proposal comes
 * back done, warning on a failed send); the composer holds the keyboard and, on a phone, the panel is sized to the
 * visual viewport so the composer stays above the on-screen keyboard. A product card opens its page by growing out of
 * the card (utils/zoom); the chat is mounted once beside the routes (ShopAgentChat in App.js), so on the way back the
 * same conversation is still here, and the screen shrinks into the card it left from.
 *
 * LUNA'S LOOK (round 2, owner 10-23 — the portal's Luna on the customer side). The chat is Luna's surface, not the
 * shop's theme: a mostly white panel, the white character on the launcher, in the glass top bar (the conversation
 * scrolls under it) and as the empty state's picture, bobbing beside the honest line while an answer is on its way.
 * A product card is a door and a subject: "Ask about this" makes it what the conversation is about (FocusBar), and
 * every send carries it as the product the shopper is looking at. The first suggestions follow what the business
 * does (agentStarters).
 *
 * ROUND 3 (owner 10-23 — match Luna in the portal). Luna is the portal's living StylonCharacter wherever she appears
 * (the top bar's answers a tap and watches the field while you type), and every answer starts with a small living
 * character of what it is about (agentChatCore `replyCharacter`). An answer uses the conversation's full width; only
 * the customer's own messages are bubbles. The composer and what sits above it (the focus bar, a picture, the
 * listening bar) are one glass DOCK floating over the conversation: its height is measured live into --agent-dock, the
 * conversation's bottom padding, so the last thing in it always clears the composer, and the thinking row is
 * scrolled fully into view when an answer starts. The composer is a round glass "+", a narrower glass field and a
 * round send (↑, light grey with nothing to send); "+" opens a small sheet with what this browser and this business
 * really have — "Search by a picture" and, only with the Web Speech API, "Voice" (dictation in the page's language).
 */
const LOGIN_LINES = {
  askPhone: { en: 'What is your phone number? I’ll send you a code.', he: 'מה מספר הטלפון שלכם? אשלח קוד.', ar: 'ما رقم هاتفكم؟ سأرسل لكم رمزًا.' },
  loginLeft: { en: 'No problem — ask me anything else.', he: 'אין בעיה — אפשר לשאול על כל דבר אחר.', ar: 'لا مشكلة — اسألوا عن أي شيء آخر.' },
  needPhone: { en: 'Please type your phone number, e.g. 050-123-4567.', he: 'הקלידו מספר טלפון, לדוגמה 050-123-4567.', ar: 'اكتبوا رقم هاتفكم، مثل 050-123-4567.' },
  codeSent: { en: 'I sent you a code — type it here.', he: 'שלחתי קוד — הקלידו אותו כאן.', ar: 'أرسلت لكم رمزًا — اكتبوه هنا.' },
  badCode: { en: 'That code doesn’t look right — try again.', he: 'הקוד לא נראה נכון — נסו שוב.', ar: 'الرمز غير صحيح — حاولوا مرة أخرى.' },
  askName: { en: 'Welcome! What’s your full name?', he: 'ברוכים הבאים! מה השם המלא?', ar: 'أهلًا! ما اسمكم الكامل؟' },
  askGender: { en: 'One more thing this business asks: female, male or other?', he: 'עוד דבר שהעסק מבקש: נקבה, זכר או אחר?', ar: 'شيء أخير يطلبه هذا العمل: أنثى، ذكر أو آخر؟' },
  signedIn: { en: 'You’re signed in.', he: 'נכנסתם לחשבון.', ar: 'تم تسجيل الدخول.' },
  tryAgain: { en: 'Something went wrong — try again in a moment.', he: 'משהו השתבש — נסו שוב בעוד רגע.', ar: 'حدث خطأ — حاولوا بعد قليل.' },
};

const lineOf = (key, lang) => (LOGIN_LINES[key] || LOGIN_LINES.tryAgain)[lang] || LOGIN_LINES[key].en;

/** The voice module speaks in its own kinds; the store's haptics (DESIGN_SYSTEM §6.7) have theirs. */
const VOICE_HAPTIC = { start: 'light', send: 'medium', lock: 'selection', cancel: 'warning' };
function voiceHaptic(kind) { haptic(VOICE_HAPTIC[kind] || 'selection'); }

/** How long the panel takes to shrink back into the launcher before it leaves (the CSS `panelOut`, --feel-base). */
export const PANEL_CLOSE_MS = 220;

/** The width at which the panel covers the whole screen (agentChat.module.css). */
const PHONE = '(max-width: 640px)';
function isPhone() {
  try {
    return !!window.matchMedia?.(PHONE)?.matches;
  } catch (_) {
    return false;
  }
}

function storeToken(token) {
  try {
    localStorage.setItem('authToken', token);
    localStorage.setItem('accessToken', token);
  } catch (_) { /* private mode */ }
}

const NO_CART = { items: [], count: 0, total: 0 };
/** Every product grid of the shop, as utils/shopPageOf names its pages: the compact launcher sits over them. */
const SHOP_GRID_PAGES = new Set(['shop_home', 'search', 'category', 'collection', 'brand']);

/** Focus the browser shows as keyboard focus (:focus-visible); false where the selector is unknown (jsdom, old engines). */
function hasKeyboardFocus(el) {
  if (!el) return false;
  try { return el.matches(':focus-visible'); } catch (_) { return false; }
}

export default function AgentChat({ salon, cartLines = [], cart = NO_CART, productName, domainMode = false }) {
  const { t, i18n } = useTranslation();
  const lang = ['he', 'ar'].includes(String(i18n.language).split('-')[0]) ? String(i18n.language).split('-')[0] : 'en';
  const dir = lang === 'en' ? 'ltr' : 'rtl';
  const slug = salon.slug || salon.username;
  const location = useLocation();
  // On a product page the launcher is the compact one, set just above the sticky cart bar (10-21 known: it covered
  // "Choose Size" on a 390 px phone).
  const onProductPage = /\/p\/[^/]+/.test(location?.pathname ?? '');
  // On every product grid too (store polish 10-28: the big one covered product names there): the shop, a search of it,
  // a category, the collections and a collection, a brand — on either tree. utils/shopPageOf says which page an address
  // is (App.js declares its routes inline, so there is no route table to match against).
  const onShopGrid = SHOP_GRID_PAGES.has(journeyPageOf(location?.pathname, location?.search, domainMode)?.pageType);
  const compact = onProductPage || onShopGrid;
  const navigate = useNavigate();
  const navigationType = useNavigationType();
  // closed → open → closing → closed. `closing` keeps the panel on screen for PANEL_CLOSE_MS while it shrinks back
  // into the launcher; the launcher is already back underneath it.
  const [phase, setPhase] = useState('closed');
  const open = phase === 'open';
  const shown = phase !== 'closed';
  // 'grow' when a tap opened it (from the launcher); 'still' when coming back from a product page reopened it — the
  // screen shrinking into the card is the motion then, and the panel must not also grow.
  const [arrival, setArrival] = useState('grow');
  const [backTo, setBackTo] = useState(null);
  const [logoFailed, setLogoFailed] = useState(false);
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const [login, setLogin] = useState(null); // { step, phone } while signing in inside the chat
  // What waits for a sign-in: the message and how it was sent (a tapped step is retried as the same action).
  const [pendingRetry, setPendingRetry] = useState(null);
  // What the customer has chosen so far — the server's (a booking, a stay), from every answer (contract r4a).
  const [selection, setSelection] = useState(null);
  // The selection's sheet: null → 'open' → 'closing' (it shrinks back into the bar) → null.
  const [sheet, setSheet] = useState(null);
  const sheetRef = useRef(sheet);
  sheetRef.current = sheet;
  const [picture, setPicture] = useState(null);
  // The product the conversation is about ("Ask about this"): { id, name } — carried by every send while it is here.
  const [focus, setFocus] = useState(null);
  // The + menu.
  const [menuOpen, setMenuOpen] = useState(false);
  const listRef = useRef(null);
  const dockRef = useRef(null);
  const thinkingRef = useRef(null);
  const plusRef = useRef(null);
  const menuRef = useRef(null);
  const menuOpenRef = useRef(menuOpen);
  menuOpenRef.current = menuOpen;
  const fileRef = useRef(null);
  const panelRef = useRef(null);
  const inputRef = useRef(null);
  // The field fits its text: one line is 44px, then it grows with each line up to three (86px with its padding), then scrolls inside.
  useLayoutEffect(() => {
    const el = inputRef.current;
    if (!el) return;
    el.style.height = 'auto';
    const h = el.scrollHeight;
    el.style.height = h > 0 ? `${Math.min(h, 86)}px` : '';
  }, [text, open]);
  const launcherRef = useRef(null);
  // ONE fixed place for the launcher: the page reserves room for it (body[data-agent-launcher], index.css), so at rest at the end of a page nothing is under it.
  useEffect(() => { document.body.setAttribute('data-agent-launcher', 'on'); return () => document.body.removeAttribute('data-agent-launcher'); }, []);
  // The launcher steps aside while the shopper scrolls down (useScrolledAway) — unless it holds the keyboard focus.
  const scrolledAway = useScrolledAway();
  const [launcherFocused, setLauncherFocused] = useState(false);
  const [reducedMotion] = useState(() => {
    try { return !!window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches; } catch (_) { return false; }
  });
  const phaseRef = useRef(phase);
  phaseRef.current = phase;

  const openPanel = useCallback(() => {
    haptic('light');
    setArrival('grow');
    setPhase('open');
  }, []);
  const closePanel = useCallback(() => setPhase((p) => (p === 'open' ? 'closing' : p)), []);
  useEffect(() => {
    if (phase !== 'closing') return undefined;
    const id = setTimeout(() => setPhase('closed'), PANEL_CLOSE_MS);
    return () => clearTimeout(id);
  }, [phase]);

  // The panel holds the keyboard like every dialog of the store: focus in (on the composer, §6.9), Escape closes,
  // focus back to the launcher, the page behind inert (the dialog guard, 10-21 — on a phone the panel is the whole
  // screen).
  // Escape closes the + menu first, then the selection's sheet, then the chat.
  const onEscape = useCallback(() => {
    if (menuOpenRef.current) setMenuOpen(false);
    else if (sheetRef.current === 'open') setSheet('closing');
    else closePanel();
  }, [closePanel]);
  useEffect(() => {
    if (sheet !== 'closing') return undefined;
    const id = setTimeout(() => setSheet(null), PANEL_CLOSE_MS);
    return () => clearTimeout(id);
  }, [sheet]);
  useDialogA11y({ active: open, containerRef: panelRef, initialFocusRef: isTouchDevice() ? null : inputRef, returnFocusRef: launcherRef, onEscape });
  const name = salon.whatsappAssistantName || t('agent.name', { defaultValue: 'Luna' });
  const auth = useAuth();

  const keepLastInView = useCallback(() => {
    if (listRef.current) listRef.current.scrollTop = listRef.current.scrollHeight;
  }, []);
  // The newest message in view — and on open too, since the panel mounts fresh each time it opens.
  useEffect(() => { keepLastInView(); }, [messages, busy, shown, focus, keepLastInView]);
  // An answer starts: the thinking row, fully in view above the dock (owner 10-23, screenshot 9: it was half under the
  // composer). The conversation's scroll padding is the dock's height, so `block: 'end'` clears it.
  useEffect(() => {
    if (busy) thinkingRef.current?.scrollIntoView?.({ block: 'end', inline: 'nearest' });
  }, [busy]);

  // The dock (the composer and what sits above it) floats over the conversation; its height, measured live, is the
  // conversation's bottom padding (--agent-dock). When it grows (a focus bar, a picture) and the conversation was at
  // its end, the end stays in view.
  useEffect(() => {
    if (!shown) return undefined;
    const dock = dockRef.current;
    const panel = panelRef.current;
    if (!dock || !panel) return undefined;
    const measure = () => {
      const h = `${Math.round(dock.offsetHeight || 0)}px`;
      if (panel.style.getPropertyValue('--agent-dock') === h) return;
      const list = listRef.current;
      const atEnd = !list || list.scrollHeight - list.scrollTop - list.clientHeight < 48;
      panel.style.setProperty('--agent-dock', h);
      if (atEnd) keepLastInView();
    };
    measure();
    const RO = typeof window !== 'undefined' ? window.ResizeObserver : null;
    if (typeof RO !== 'function') return undefined;
    const ro = new RO(measure);
    ro.observe(dock);
    return () => ro.disconnect();
  }, [shown, keepLastInView]);

  // A tap anywhere outside the + menu closes it.
  useEffect(() => {
    if (!menuOpen) return undefined;
    const away = (e) => {
      if (menuRef.current?.contains(e.target) || plusRef.current?.contains(e.target)) return;
      setMenuOpen(false);
    };
    document.addEventListener('pointerdown', away, true);
    return () => document.removeEventListener('pointerdown', away, true);
  }, [menuOpen]);

  // A first-ever visitor has no signed visitor key until the server answers a question; voice needs it, so it is asked for
  // when the chat opens (a failed ask changes nothing: the chat works, and the first answer brings the key as before).
  useEffect(() => {
    if (phase !== 'open' || readVisitorKey(slug)) return;
    let live = true;
    Promise.resolve().then(() => mintAgentVisitorKey(slug)).then((res) => { if (live && res?.visitorKey && !readVisitorKey(slug)) saveVisitorKey(slug, res.visitorKey); }).catch(() => {});
    return () => { live = false; };
  }, [phase, slug]);

  // The + menu never outlives the open chat.
  useEffect(() => { if (phase !== 'open') setMenuOpen(false); }, [phase]);

  // The sign-in step changed (phone → code → name, or left): the composer is the field to type in again (§6.9).
  const step = login?.step ?? null;
  const stepSeen = useRef(step);
  useEffect(() => {
    if (stepSeen.current === step) return;
    stepSeen.current = step;
    inputRef.current?.focus({ preventScroll: true });
  }, [step]);

  // The on-screen keyboard (§6.9). On a phone the panel is the whole screen, and iOS does not shrink a fixed element
  // when its keyboard opens — it covers the composer. So the panel is sized to the VISUAL viewport (the CSS reads
  // --agent-vv-height / --agent-vv-top under the phone media query), and the last message stays in view while typing.
  useEffect(() => {
    if (!shown) return undefined;
    const vv = typeof window !== 'undefined' ? window.visualViewport : null;
    const panel = panelRef.current;
    if (!vv || !panel) return undefined;
    const fit = () => {
      panel.style.setProperty('--agent-vv-height', `${Math.round(vv.height)}px`);
      panel.style.setProperty('--agent-vv-top', `${Math.round(vv.offsetTop || 0)}px`);
      // The keyboard is up when the visible part is much shorter than the window (iOS never shrinks the window).
      panel.setAttribute('data-keyboard', window.innerHeight - vv.height > 120 ? 'open' : 'closed');
      if (document.activeElement === inputRef.current) keepLastInView();
    };
    fit();
    vv.addEventListener('resize', fit);
    vv.addEventListener('scroll', fit);
    // iOS slides the keyboard in and out WITHOUT telling the page every frame (the viewport events come at the start
    // and the end), so for a moment after the field gains or loses focus the panel follows the viewport itself — the
    // frame that was stale was the transparent band between the field and the keys (owner 10-25).
    let follow = null;
    const followFor = (ms) => {
      clearInterval(follow);
      const until = Date.now() + ms;
      follow = setInterval(() => { fit(); if (Date.now() > until) { clearInterval(follow); follow = null; } }, 50);
    };
    const input = inputRef.current;
    const onFocusChange = () => followFor(700);
    input?.addEventListener('focus', onFocusChange);
    input?.addEventListener('blur', onFocusChange);
    return () => {
      clearInterval(follow);
      input?.removeEventListener('focus', onFocusChange);
      input?.removeEventListener('blur', onFocusChange);
      vv.removeEventListener('resize', fit);
      vv.removeEventListener('scroll', fit);
    };
  }, [shown, keepLastInView]);

  // The conversation the server named on the last answer — the thumbs name it back, and the server checks it is theirs.
  const [conversationId, setConversationId] = useState('');
  const push = useCallback((m) => setMessages((list) => [...list, { id: `${Date.now()}-${Math.random()}`, ...m }]), []);
  const productHref = useCallback((id) => (domainMode ? `/p/${id}` : `/${slug}/p/${id}`), [domainMode, slug]);
  const here = `${location.pathname}${location.search}`;
  // The storefront checkout of the same cart (the cart drawer's own way there).
  const checkoutHref = domainMode ? '/checkout' : `/${slug}/checkout`;
  // A page the selection hands over to (the stay's request sheet, the checkout): in domain mode without the slug; on a
  // phone the panel closes first — it covers the screen.
  const openSelectionUrl = useCallback((url) => {
    const raw = String(url || '');
    if (!raw.startsWith('/')) return;
    const to = domainMode && raw.startsWith(`/${slug}/`) ? raw.slice(slug.length + 1) : raw;
    haptic('selection');
    if (isPhone()) setPhase('closed');
    navigate(to);
  }, [domainMode, navigate, slug]);

  // A product card opens its page by growing out of the card (§6.4). On a phone the panel covers the screen, so it
  // closes in the same update — flushSync, so the view transition captures the page with the panel already gone.
  const openProduct = useCallback((messageId, p, card) => {
    const to = productHref(p.id);
    const phone = isPhone();
    rememberOrigin({ key: `agent-product:${messageId}:${p.id}`, path: here, to, reopen: phone });
    zoomInto(card, () => flushSync(() => {
      if (phone) setPhase('closed');
      navigate(to);
    }));
  }, [here, navigate, productHref]);

  // Coming back (§6.5): the browser's back to the page the card was tapped on reopens the chat where a phone closed it,
  // and the screen shrinks into the same card. Going on anywhere else forgets the way back.
  useEffect(() => {
    const o = takeOrigin();
    if (!o) return;
    if (location.pathname === o.to) {
      rememberOrigin(o);
      return;
    }
    if (here !== o.path || navigationType !== 'POP') return;
    if (o.reopen) {
      setArrival('still');
      setPhase('open');
    } else if (phaseRef.current !== 'open') {
      return;
    }
    setBackTo(o.key);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [here]);
  useEffect(() => {
    if (!backTo || phase !== 'open') return;
    setBackTo(null);
    const card = Array.from(panelRef.current?.querySelectorAll('[data-zoom-key]') || []).find((el) => el.getAttribute('data-zoom-key') === backTo);
    if (!card) return;
    card.scrollIntoView?.({ block: 'nearest', inline: 'nearest' });
    zoomBackTo(card);
  }, [backTo, phase]);

  const send = useCallback(async (message, opts = {}) => {
    const body = String(message ?? '').trim();
    if ((!body && !opts.image) || busy) return;
    if (!opts.silent) push({ role: 'user', text: body || '📷', image: opts.image });
    setBusy(true);
    try {
      const res = await sendAgentMessage(slug, {
        message: body || (lang === 'he' ? 'יש לכם משהו כזה?' : lang === 'ar' ? 'هل لديكم شيء مثل هذا؟' : 'Do you have something like this?'),
        // The reply follows the person: a voice turn says the language it was SPOKEN in, a typed one the language of its
        // own words, a tapped button the page's. (The page's language is only the fallback, never the rule.)
        language: opts.language || (opts.via ? lang : languageOf(body) || lang),
        ...(opts.via ? { via: opts.via } : {}),
        ...(opts.action ? { action: opts.action } : {}),
        ...(opts.image ? { image: opts.image } : {}),
        page: agentPageContext(location, { productName, focus }, cartLines),
        visitorKey: readVisitorKey(slug),
      });
      if (res.visitorKey) saveVisitorKey(slug, res.visitorKey);
      if (res.conversationId) setConversationId(res.conversationId);
      if (res && 'selection' in res) setSelection(res.selection ?? null);
      push({ role: 'assistant', rateable: true, text: res.reply, details: res.details, choices: res.choices, items: res.items, confirmation: res.confirmation });
      if (res.needsLogin) {
        setPendingRetry({ message: body, opts: { ...(opts.via ? { via: opts.via } : {}), ...(opts.action ? { action: opts.action } : {}) } });
        setLogin({ step: 'phone' });
        push({ role: 'assistant', text: lineOf('askPhone', lang) });
      }
      return { replyId: res.replyId, language: res.language };
    } catch (_) {
      haptic('warning');
      push({ role: 'assistant', text: lineOf('tryAgain', lang) });
      return null;
    } finally {
      setBusy(false);
    }
  }, [busy, cartLines, focus, lang, location, productName, push, slug]);

  const onLoginInput = useCallback(async (input) => {
    push({ role: 'user', text: login.step === 'code' ? '••••' : input });
    setBusy(true);
    try {
      const out = await loginStep(login, input, { normalizePhone, sendOtp, verifyOtp, onboardCustomer }, { language: lang, salonId: salon.id || salon._id, genderPolicy: salon.customerGenderAtSignup });
      if (out.token) storeToken(out.token);
      push({ role: 'assistant', text: lineOf(out.say, lang) });
      if (out.state.step === 'done') {
        setLogin(null);
        try {
          const me = out.customer || (await getMe());
          // Published to the AuthContext too, so the rest of the store knows the customer without a reload.
          if (me && auth?.login) auth.login(me);
          else if (me) localStorage.setItem('customer', JSON.stringify(me));
        } catch (_) { /* the token is enough to continue */ }
        const retry = pendingRetry;
        setPendingRetry(null);
        if (retry) setTimeout(() => send(retry.message, { ...retry.opts, silent: true }), 300);
      } else {
        setLogin(out.state);
      }
    } catch (_) {
      // A step that threw says so instead of leaving the chat waiting (final review 10-21).
      haptic('warning');
      push({ role: 'assistant', text: lineOf('tryAgain', lang) });
    } finally {
      setBusy(false);
    }
  }, [auth, lang, login, pendingRetry, push, salon, send]);

  // Signing in can be left (10-21 deferred: no way back): the chat goes on as before, and nothing waits for it.
  const leaveLogin = useCallback(() => {
    setLogin(null);
    setPendingRetry(null);
    setText('');
    push({ role: 'assistant', text: lineOf('loginLeft', lang) });
  }, [lang, push]);

  const decide = useCallback(async (proposalId, decision) => {
    setBusy(true);
    setMessages((list) => list.map((m) => (m.confirmation?.proposalId === proposalId ? { ...m, confirmation: { ...m.confirmation, decided: decision } } : m)));
    // The customer's own mark of what they decided, on their side, before the reply it leads to (owner 10-23).
    push({ role: 'user', decision, text: decision === 'confirm' ? t('agent.decisionApproved', { defaultValue: 'Approved' }) : t('agent.decisionCancelled', { defaultValue: 'Cancelled' }) });
    try {
      const res = await decideAgentProposal(slug, proposalId, decision, lang);
      // What you confirmed is done (§6.7) — the server says it executed, not merely that it answered: a refusal (the
      // slot was taken) comes back 200 with nothing executed (review 10-22).
      if (decision === 'confirm') haptic(res?.executed ? 'success' : 'warning');
      // What came back is a reply like any other: choices, cards, a next card to approve (the rest of a message).
      if (res && 'selection' in res) setSelection(res.selection ?? null);
      push({ role: 'assistant', rateable: true, text: res.reply, details: res.details, choices: res.choices, items: res.items, confirmation: res.confirmation });
    } catch (e) {
      const signIn = e?.response?.status === 401 || e?.response?.status === 403;
      if (!signIn) haptic('warning');
      push({ role: 'assistant', text: signIn ? lineOf('askPhone', lang) : lineOf('tryAgain', lang) });
    } finally {
      setBusy(false);
    }
  }, [lang, push, slug, t]);

  const submit = (e) => {
    e.preventDefault();
    const value = text;
    if (!value.trim() && !picture) return;
    haptic('medium');
    setText('');
    if (login) onLoginInput(value);
    else if (picture) {
      const image = picture;
      setPicture(null);
      send(value, { image });
    } else send(value);
  };

  const onPickImage = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    try {
      setPicture(await shrinkImage(file));
      inputRef.current?.focus({ preventScroll: true });
    } catch (_) {
      push({ role: 'assistant', text: t('agent.notAPicture', { defaultValue: 'That file is not a picture I can read.' }) });
    }
  };

  // VOICE (owner redesign 26 Oct). Every customer has the clip ears (a note through the capped transcribe door); the
  // visitor key rides with it. A held note is sent on release in the language it was SPOKEN in; voice mode is the
  // hands-free loop; the reply is spoken only if the server says it is one it gave THIS visitor (its replyId).
  const transcribe = useCallback((blob) => transcribeAgentVoice(slug, blob, { visitorKey: readVisitorKey(slug) }), [slug]);
  const synthesize = useCallback((args) => synthesizeAgentReply(slug, { replyId: args.replyId, part: args.part, visitorKey: readVisitorKey(slug), signal: args.signal }), [slug]);
  const sendSpoken = useCallback(async (words, said) => {
    const res = await send(words, { language: said });
    return res?.replyId ? { key: res.replyId, replyId: res.replyId, language: res.language } : null;
  }, [send]);
  const voice = useVoiceConversation({ language: lang, send: sendSpoken, transcribe, synthesize, haptic: voiceHaptic });
  const hold = useHoldToRecord({ language: lang, transcribe, haptic: voiceHaptic, onSend: (words, said) => { send(words, { language: said }); } });
  // The page behind the chat never scrolls under it, and the page's own bars do not cover it.
  useBodyScrollLock(shown);
  useEffect(() => { if (phase !== 'open') { voice.stop(); hold.cancel(); } }, [phase]); // eslint-disable-line react-hooks/exhaustive-deps
  const listening = voice.state === 'listening' || voice.state === 'hearing' || hold.phase === 'recording' || hold.phase === 'locked';
  // Luna is voice mode's face: attentive while listening, brighter ring while hearing, eyes up while thinking, swelling
  // while speaking, half-shut and badged while muted, neutral when it could not hear.
  const voiceFigure = useCallback(({ state, level }) => (
    <LunaCharacter
      size={84}
      rtl={dir === 'rtl'}
      watchPointer={false}
      listening={state === 'listening' || state === 'hearing'}
      loading={state === 'thinking' || state === 'connecting'}
      speaking={state === 'speaking'}
      speakingAmplitude={state === 'speaking' ? 0.5 + Math.min(0.5, level * 4) : 0}
      halfShut={state === 'muted'}
      neutral={state === 'failed' || state === 'muted'}
      className={styles.voiceLuna}
    />
  ), [dir]);
  const startVoiceMode = () => { setMenuOpen(false); voice.start(); };
  const pickFromMenu = (action) => {
    haptic('selection');
    setMenuOpen(false);
    if (action === 'picture') fileRef.current?.click();
  };

  // A tapped option: its action when the server gave it one (a step of a booking — no model call), else its message.
  const choose = (option) => {
    haptic('selection');
    if (option?.action) send(option.label, { via: 'action', action: option.action });
    else send(option?.message, { via: 'choice' });
  };
  // A step the selection offers (its next step, a change, a removal) — sent as its action under its label.
  const act = useCallback((label, action) => {
    if (!action) return;
    haptic('selection');
    send(label, { via: 'action', action });
  }, [send]);
  const suggest = (message) => {
    haptic('selection');
    send(message);
  };
  const askAbout = (p) => setFocus({ id: String(p.id), name: p.name || '' });
  const askAboutService = (sv) => setFocus({ kind: 'service', id: String(sv.id), name: sv.name || '' });
  const askAboutStay = (st) => setFocus({ kind: 'stay', id: String(st.slug), name: st.name || '' });
  // "Book" on a service card adds it to the booking in progress; "Book a stay" starts a stay request — the server's steps.
  const bookService = (sv) => act(t('agent.bookService', { defaultValue: 'Book {{name}}', name: sv.name }), { type: 'book_service', serviceId: sv.id });
  const bookStay = (st) => act(t('agent.bookStayAt', { defaultValue: 'Book a stay at {{name}}', name: st.name }), { type: 'stay_start', propertySlug: st.slug });
  // An appointment card's "Add to calendar": the store's own calendar file, with the business and its address.
  const toCalendar = (a) => addToCalendar({
    title: [a.services, salon.name].filter(Boolean).join(' — '),
    description: a.specialist || '',
    location: [salon.address, salon.city].filter(Boolean).join(', '),
    startsAt: a.startsAt,
    endsAt: a.endsAt,
  });
  const stayHref = useCallback((propertySlug) => (domainMode ? `/stay/${propertySlug}` : `/${slug}/stay/${propertySlug}`), [domainMode, slug]);
  // "Add to cart" on a card: the storefront's own cart (agentCart), and a short line of the chat's own saying so — it is
  // never sent (owner 10-23).
  const addToCart = (p, option) => {
    const name = addToStorefrontCart(slug, p, option);
    if (!name) return;
    haptic('success');
    push({ role: 'assistant', text: t('agent.addedToCart', { defaultValue: 'Added {{name}} to your cart.', name }), character: 'products', local: true });
  };

  // "Ask on WhatsApp" on an ask-us card: the page's own link for the shop's number, and the product page's own ask
  // draft — the booking, with the number and the date to fill in (panel S6). The tap is the order button's own event,
  // so the shop's funnel counts it.
  const askHrefFor = (p) => contactOrderChannels(salon, t('productDetail.askPrefill', { product: p.name, defaultValue: 'Hello, I would like to book “{{product}}”: quantity ___ and date ___' })).whatsappUrl;
  const askedOnWhatsapp = (p) => {
    if (salon?.id) trackStoreEvent({ salonId: salon.id, eventType: 'shop_contact_order', payload: { channel: 'whatsapp', where: 'agent', ask: true, productId: p.id, variantId: p.variantId ?? null } });
  };

  const rtl = dir === 'rtl';
  const canPicture = !login && !!salon.productsEnabled;
  const canVoice = !login && canRecordClips();
  // Send takes the action slot once there is something to send (and is the only action where nothing can record).
  const sendMode = text.trim().length > 0 || !!picture || !canVoice;
  const canPlus = canPicture;
  // Luna watches the field while something is typed in it, her eyes following the end of the line.
  const typing = !busy && !listening && text.trim().length > 0;
  const typingProgress = Math.min(1, text.length / 36);

  // One answer of a reply: its own small character (round 3), its text, and what it carries.
  const renderPart = (m, isLast = true) => (
    <div key={m.id} className={styles.replyPart} data-testid="agent-reply-part">
      {isLast && <StylonCharacter kind={replyCharacter(m)} size={26} rtl={rtl} watchPointer={false} testId="agent-reply-character" className={styles.replyCharacter} />}
      <ReplyText text={m.text} details={m.details} t={t} />
      {m.choices?.options?.length ? (
        <div className={styles.pills}>
          {m.choices.options.map((o) => (
            <button key={o.label} type="button" className={styles.pill} disabled={busy} onClick={() => choose(o)}>{o.label}</button>
          ))}
        </div>
      ) : null}
      {m.items?.products?.length ? (
        <ProductStrip products={m.items.products} currency={salon.currency} hrefFor={productHref} zoomKeyFor={(p) => `agent-product:${m.id}:${p.id}`} onOpen={(p, card) => openProduct(m.id, p, card)} onAsk={askAbout} onAdd={addToCart} askHrefFor={askHrefFor} onAskOnWhatsapp={askedOnWhatsapp} t={t} />
      ) : null}
      {m.items?.services?.length ? <ServiceStrip services={m.items.services} currency={salon.currency} onBook={bookService} onAsk={askAboutService} busy={busy} t={t} /> : null}
      {m.items?.stays?.length ? <StayStrip stays={m.items.stays} currency={salon.currency} hrefFor={stayHref} onOpen={openSelectionUrl} onBook={bookStay} onAsk={askAboutStay} busy={busy} t={t} /> : null}
      {m.items?.appointments?.length ? <AppointmentCards appointments={m.items.appointments} onAct={act} onCalendar={toCalendar} busy={busy} t={t} /> : null}
      {m.items?.orders?.length ? <OrderCards orders={m.items.orders} hrefFor={(p) => p} onOpen={openSelectionUrl} onAct={act} busy={busy} t={t} /> : null}
      {(m.items?.places || []).map((pl) => (
        <div key={pl.address} className={styles.place}>
          <div className={styles.placeAddress}>📍 {pl.address}</div>
          <div className={styles.placeButtons}>
            <a href={pl.waze} target="_blank" rel="noopener noreferrer" className={styles.linkButton}>Waze</a>
            <a href={pl.maps} target="_blank" rel="noopener noreferrer" className={styles.linkButton}>{t('agent.maps', { defaultValue: 'Google Maps' })}</a>
          </div>
        </div>
      ))}
      {(m.items?.links || []).length ? (
        <div className={styles.placeButtons}>
          {m.items.links.map((l) => (
            // A page of this store (the checkout of the same cart, the booking page) opens in the app, in domain mode
            // without the slug (round 4); an outside link opens beside it.
            <a key={l.url} href={l.url} className={l.label === 'checkout' ? styles.primaryButton : styles.linkButton} onClick={l.url.startsWith('/') ? (e) => { e.preventDefault(); openSelectionUrl(l.url); } : undefined} {...(l.url.startsWith('http') ? { target: '_blank', rel: 'noopener noreferrer' } : {})}>
              {l.label === 'checkout' ? t('agent.checkout', { defaultValue: 'Checkout' }) : l.label === 'call' ? t('agent.call', { defaultValue: 'Call' }) : l.label === 'booking' ? t('agent.bookingPage', { defaultValue: 'Booking page' }) : t('agent.page', { defaultValue: 'Our page' })}
            </a>
          ))}
        </div>
      ) : null}
      {m.confirmation ? (
        <div className={styles.confirm} data-testid="agent-confirm">
          <p className={styles.confirmText}>{m.confirmation.summaryText}</p>
          {m.confirmation.decided ? null : (
            <div className={styles.placeButtons}>
              <button type="button" className={styles.primaryButton} disabled={busy} onClick={() => decide(m.confirmation.proposalId, 'confirm')}>{t('agent.confirm', { defaultValue: 'Confirm' })}</button>
              <button type="button" className={styles.linkButton} disabled={busy} onClick={() => decide(m.confirmation.proposalId, 'cancel')}>{t('agent.change', { defaultValue: 'Change' })}</button>
            </div>
          )}
        </div>
      ) : null}
    </div>
  );

  const logo = !logoFailed && (salon.logoUrl || salon.logo) ? toImageUrl(salon.logoUrl || salon.logo) : '';
  const panelClass = [styles.panel, phase === 'closing' ? styles.panelClosing : '', arrival === 'still' ? styles.panelStill : ''].filter(Boolean).join(' ');

  // The launcher stays mounted: hidden while the panel is open, back underneath it the moment it starts to close —
  // the panel shrinks into it, and focus returns to it. It carries the panel's direction, so both sit in one corner.
  // While the shopper scrolls down it steps aside (store polish 10-28: on a phone it covered product names): it slides
  // toward its own edge and fades, cannot be tapped, and leaves the tab order and the accessibility tree. It comes back
  // on a scroll up or at either end of the page, never at rest over a card. Never away while the chat is open or closing
  // (the panel shrinks into it), nor while it holds KEYBOARD focus (:focus-visible, read when the decision is made): the
  // focus a tap leaves on it — closing the chat hands it back — does not pin it, and goes when it steps aside, so
  // aria-hidden never sits on the focused element. It has no unread badge to keep it.
  // Reduced motion: no transition class, so the change is immediate and nothing travels.
  const away = scrolledAway && !shown && !(launcherFocused && hasKeyboardFocus(launcherRef.current));
  useEffect(() => {
    const el = launcherRef.current;
    if (away && el && document.activeElement === el) el.blur();
  }, [away]);
  // Compact is the size; the product page's lower seat over its cart bar is its own class, so on a grid she keeps the
  // dock's baseline.
  const launcherClass = [
    styles.launcher,
    compact ? styles.launcherCompact : '',
    onProductPage ? styles.launcherOverCartBar : '',
    reducedMotion ? '' : styles.launcherMoves,
    away ? styles.launcherAway : '',
  ].filter(Boolean).join(' ');
  const launcher = (
    <button ref={launcherRef} type="button" dir={dir} hidden={open} className={launcherClass} aria-hidden={away ? 'true' : undefined} tabIndex={away ? -1 : undefined} onFocus={() => setLauncherFocused(true)} onBlur={() => setLauncherFocused(false)} onClick={openPanel} aria-label={t('agent.open', { defaultValue: 'Chat with us' })} data-testid="agent-launcher">
      <LunaCharacter size={compact ? 44 : 52} watchPointer={false} />
    </button>
  );
  if (!shown) return launcher;

  return (
    <>
    {launcher}
    <div ref={panelRef} className={panelClass} dir={dir} role="dialog" aria-modal="true" aria-label={name} aria-hidden={phase === 'closing' ? 'true' : undefined} data-testid="agent-panel">
      {/* The glass top bar (round 2): the conversation scrolls under it. Luna, who is talking, and for which business. */}
      <div className={styles.header} data-testid="agent-header">
        <LunaCharacter size={40} interactive rtl={rtl} loading={busy} listening={listening} typing={typing} typingProgress={typingProgress} className={styles.headerLuna} />
        <div className={styles.headerText}>
          <div className={styles.title}>{name}</div>
          <div className={styles.subtitle}>
            {logo ? <img className={styles.headerLogo} src={logo} alt="" onError={() => setLogoFailed(true)} /> : null}
            <span className={styles.subtitleName}>{salon.name}</span>
          </div>
        </div>
        <button type="button" className={styles.close} onClick={closePanel} aria-label={t('agent.close', { defaultValue: 'Close' })}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" aria-hidden="true" focusable="false"><path d="M6 6l12 12" /><path d="M18 6L6 18" /></svg>
        </button>
      </div>

      <div className={styles.messages} ref={listRef} data-testid="agent-messages">
        {/* In voice mode the only Luna on screen is the voice figure: the welcome steps aside. */}
        {!messages.length && !voice.active && (
          // The empty state (§6.10): Luna, who is talking, one line, and the quick selections — what this business does.
          <div className={styles.welcome} data-testid="agent-empty">
            <LunaCharacter size={84} interactive rtl={rtl} typing={typing} typingProgress={typingProgress} listening={listening} className={styles.welcomeLuna} />
            <h2 className={styles.emptyTitle}>{t('agent.emptyTitle', { name, defaultValue: `Hi, I’m ${name}` })}</h2>
            <p className={styles.emptyLine}>{t('agent.emptyLine', { defaultValue: 'Ask me anything — I’ll answer right here.' })}</p>
            <div className={styles.suggestions}>
              {agentStarters(salon, t).map((s) => (
                <button key={s.key} type="button" onClick={() => suggest(s.label)}>{s.label}</button>
              ))}
            </div>
          </div>
        )}
        {groupMessages(messages).map((g) => {
          const first = g.parts[0];
          if (g.kind === 'mark') {
            // The customer's Confirm / Change on a card: a small mark on their side, not a typed bubble (owner 10-23).
            return (
              <div key={first.id} className={styles.userRow}>
                <span className={styles.decisionMark} data-testid="agent-decision-mark" data-decision={first.decision}>
                  {first.decision === 'confirm' ? <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" focusable="false"><path d="M5 12.5l4.5 4.5L19 7.5" /></svg> : null}
                  {first.text}
                </span>
              </div>
            );
          }
          if (g.kind === 'user') {
            return (
              <div key={first.id} className={styles.userRow}>
                <div className={styles.userBubble}>
                  {first.image ? <img src={first.image} alt="" className={styles.sentPicture} /> : null}
                  <p className={styles.para} dir="auto">{first.text}</p>
                </div>
              </div>
            );
          }
          // Answers in a row are one reply: each part keeps its own character, a slight space between parts.
          return (
            <div key={first.id} className={styles.botRow} data-testid="agent-reply">
              <div className={styles.botBubble}>
                {g.parts.map((m, k) => renderPart(m, k === g.parts.length - 1))}
                {(() => {
                  // The thumbs rate the last answer of the reply that is the agent's own (a sign-in ask is the widget's).
                  const rated = [...g.parts].reverse().find((m) => m.rateable);
                  if (!rated || !rated.text) return null;
                  const at = messages.findIndex((x) => x.id === g.parts[0].id);
                  const asked = [...messages.slice(0, Math.max(at, 0))].reverse().find((x) => x.role === 'user' && !x.decision);
                  return <ReplyFeedback key={rated.id} slug={slug} turnId={String(rated.id).slice(0, 64)} conversationId={conversationId} userMessage={asked?.text || ''} reply={rated.text} language={lang} visitorKey={readVisitorKey(slug)} />;
                })()}
              </div>
            </div>
          );
        })}
        {/* While an answer is on its way: Luna bobbing beside the honest line, where the answer will appear. */}
        {busy && (
          <div className={styles.botRow} ref={thinkingRef}>
            <div className={styles.thinkingRow} data-testid="agent-thinking">
              <LunaCharacter size={30} loading rtl={rtl} watchPointer={false} />
              <ThinkingLine t={t} />
            </div>
          </div>
        )}
      </div>

      {/* THE DOCK (round 3): the composer and what sits above it, glass over the conversation. */}
      <div className={styles.dock} ref={dockRef} data-testid="agent-dock">
        {/* What is chosen so far, the running price and the next step (owner 10-23) — hidden while signing in. */}
        {login ? null : <SelectionBar selection={selection} cart={cart} currency={salon.currency} busy={busy} onAct={act} onOpenUrl={openSelectionUrl} checkoutHref={checkoutHref} sheet={sheet} onSheet={setSheet} t={t} />}
        {/* Hidden while signing in: what is typed then is the phone / the code, not a question. */}
        <FocusBar focus={login ? null : focus} onSend={(message, opts) => send(message, opts)} onClear={() => setFocus(null)} disabled={busy} t={t} />

        {picture ? (
          <div className={styles.pictureBar} data-testid="agent-picture">
            <img src={picture} alt="" />
            <span>{t('agent.pictureReady', { defaultValue: 'I’ll look for something like this' })}</span>
            <button type="button" onClick={() => setPicture(null)} aria-label={t('agent.removePicture', { defaultValue: 'Remove the picture' })}>×</button>
          </div>
        ) : null}

        {/* A note in the making: the live words above the field, a clock and a level, what the finger may do. */}
        {voice.active ? null : <RecordingStrip recorder={hold} language={lang} />}
        {voice.active ? (
          <VoiceModeView voice={voice} language={lang} figure={voiceFigure} onType={() => inputRef.current?.focus({ preventScroll: true })} />
        ) : (
        <form className={styles.composer} onSubmit={submit}>
          {canPicture ? <input ref={fileRef} type="file" accept="image/*" hidden onChange={onPickImage} data-testid="agent-picture-input" /> : null}
          {menuOpen && canPlus ? (
            // A small sheet above the composer: only what this browser and this business really have.
            <div ref={menuRef} className={styles.menu} role="menu" aria-label={t('agent.moreOptions', { defaultValue: 'More options' })}>
              {canPicture ? (
                <button type="button" role="menuitem" className={styles.menuItem} onClick={() => pickFromMenu('picture')}>
                  <span className={styles.menuIcon} aria-hidden="true">
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" focusable="false"><path d="M4 8.5A2.5 2.5 0 0 1 6.5 6h1.8l1.4-2h4.6l1.4 2h1.8A2.5 2.5 0 0 1 20 8.5v8A2.5 2.5 0 0 1 17.5 19h-11A2.5 2.5 0 0 1 4 16.5z" /><circle cx="12" cy="12.5" r="3.5" /></svg>
                  </span>
                  {t('agent.searchByPicture', { defaultValue: 'Search by a picture' })}
                </button>
              ) : null}
            </div>
          ) : null}
          {login ? (
            <button type="button" className={styles.notNow} onClick={leaveLogin} disabled={busy}>{t('agent.notNow', { defaultValue: 'Not now' })}</button>
          ) : canPlus ? (
            <button
              ref={plusRef}
              type="button"
              className={`${styles.iconButton} ${menuOpen ? styles.iconButtonOpen : ''}`}
              onClick={() => { haptic('selection'); setMenuOpen((o) => !o); }}
              aria-label={t('agent.moreOptions', { defaultValue: 'More options' })}
              aria-haspopup="menu"
              aria-expanded={menuOpen ? 'true' : 'false'}
            >
              <svg className={styles.plusIcon} width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" aria-hidden="true" focusable="false"><path d="M12 5v14" /><path d="M5 12h14" /></svg>
            </button>
          ) : null}
          <div className={styles.composerField}>
            <textarea
              ref={inputRef}
              className={styles.input}
              rows={1}
              // The text's own direction: English typed on a Hebrew page sits left, as in the bubbles (simulator 26 Oct).
              dir="auto"
              onFocus={keepLastInView}
              value={text}
              onChange={(e) => setText(e.target.value)}
              // Enter sends, Shift+Enter is a new line (not while an IME is composing).
              onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent?.isComposing) { e.preventDefault(); e.currentTarget.form?.requestSubmit?.(); } }}
              placeholder={login ? (login.step === 'code' ? t('agent.codePlaceholder', { defaultValue: 'The code' }) : login.step === 'name' ? t('agent.namePlaceholder', { defaultValue: 'Full name' }) : t('agent.phonePlaceholder', { defaultValue: 'Phone number' })) : listening ? t('agent.listening', { defaultValue: 'Listening…' }) : t('agent.placeholder', { defaultValue: 'Ask anything…' })}
              inputMode={login?.step === 'code' ? 'numeric' : login?.step === 'phone' ? 'tel' : 'text'}
              maxLength={2000}
              aria-label={t('agent.placeholder', { defaultValue: 'Ask anything…' })}
              readOnly={hold.phase !== 'idle'}
            />
          </div>
          {/* ONE action button in one place (WhatsApp): the mic while there is nothing to send, Send once there is text or a
              picture. Stacked, crossfading (150 ms); the one not offered is hidden from touch and readers. */}
          <div className={styles.actionSlot} data-testid="composer-action" data-mode={sendMode ? 'send' : 'mic'}>
            {canVoice ? (
              <span className={`${styles.actionFace} ${styles.actionMic}`} aria-hidden={sendMode ? 'true' : undefined}>
                <VoiceButton recorder={hold} onTap={startVoiceMode} language={lang} disabled={busy || sendMode} className={styles.micButton} />
              </span>
            ) : null}
            <span className={`${styles.actionFace} ${styles.actionSend}`} aria-hidden={sendMode ? undefined : 'true'}>
              {/* Send: a solid circle with ↑ (up is up in every direction — never flipped). */}
              <button type="submit" data-testid="composer-send" className={styles.sendButton} disabled={busy || (!text.trim() && !picture)} tabIndex={sendMode ? undefined : -1} aria-label={t('agent.send', { defaultValue: 'Send' })}>
                <svg className={styles.sendIcon} width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" focusable="false"><path d="M12 19V5" /><path d="M6 11l6-6 6 6" /></svg>
              </button>
            </span>
          </div>
        </form>
        )}
      </div>
    </div>
    </>
  );
}
